<script>
	/* eslint-disable no-unused-vars */
	// NPM Modules
	import { afterUpdate, onDestroy, onMount, tick } from 'svelte';
	import Link from 'svelte-navaid/Link.svelte';
	import collect from 'collect.js';
	// Stores
	import SportEventStore from '@/Stores/Sport/SportEventStore.js';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore.js';
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportBetLegStore from '@/Stores/Sport/BetLegStore.js';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	import SportcastSelectionStore from '@/Stores/Sport/SportcastSelectionStore.js';
	import DerivedMarketGroupStore from '@/Stores/Sport/DerivedMarketGroupStore.js';
	// Components
	import Helmet from '@/Components/Helmet.svelte';
	import Loading from '@/Components/Loading.svelte';
	import Polling from '@/Components/Polling.svelte';
	import Icon from '@/Components/Icon.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	// Partials
	import LeftMenu from '@/Partials/Sport/Dashboard/LeftMenu.svelte';
	import EventWithKeyMarkets from '@/Partials/Sport/Event/EventWithKeyMarkets.svelte';
	import ManageMarket from '@/Partials/Sport/Markets/_ManageMarket.svelte';
	import ManageSGMMarket from '@/Partials/Sport/Markets/_ManageSGMMarket.svelte';
	import ManualResult from '@/Partials/Sport/Markets/ManualResult/_ManualResult.svelte';
	import CreateMarket from '@/Partials/Sport/Markets/_CreateMarket.svelte';
	import AllMarketControls from '@/Partials/Sport/Markets/_AllMarketsControls.svelte';
	import PromotionButton from '@/Partials/Promotions/PromotionButton.svelte';
	// Services
	import Sockets from '@/Services/Sockets.js';
	import { paginationBuilder } from '@/Services/Helpers.js';
	// Constants
	import { BetCategory } from '@/Constants/Bet';
	import { EventStatus } from '@/Constants/Event';
	import { Status, Status as MarketStatus } from '@/Constants/Market';
	import { SportSelectionStatus } from '@/Constants/SportSelection';
	import { PromotionsType } from '@/Constants/Promotions.js';

	export let event_id;

	const StatusColours = {
		[Status.Open]: 'green-500',
		[Status.Suspended]: 'orange-400',
		[Status.Closed]: 'red-600',
		[Status.Pending]: 'gray-500',
		[Status.Resulted]: 'purple-500'
	};

	$: loading = event_id && resetEvent() && getEventMarketGroups();
	$: event = loading && event_id && $SportEventStore && $SportEventStore.firstWhere('id', event_id);
	$: event && setEventSportId(event); // try and stop this running everytime the sport event store reloads
	$: event_sport_id &&
		CompetitionStore.getCompetitionsForSport(event_sport_id) &&
		DerivedMarketGroupStore.getDerivedMarketGroupsForSport(event_sport_id);
	$: hasSGMMapped = event && !!event.sportcast_fixture_id;
	$: markets =
		marketGroupId && ($MarketGroupStore || !$MarketGroupStore) && ($MarketStore || !$MarketStore) && getMarkets();
	$: $MarketGroupStore && !keyMarketGroupId && setupKeyMarketGroup();
	$: sgmLinks = $SportcastSelectionStore.count() && paginationBuilder(SportcastSelectionStore.getPagination());
	$: numberOfSGMPages =
		($SportcastSelectionStore.count() &&
			SportcastSelectionStore.getPagination() &&
			SportcastSelectionStore.getPagination().total_pages) ??
		0;
	$: $MarketGroupStore && checkForFailedLoad();

	let onlyShowMarketsWithBets = false;
	let onlyShowManualMarkets = false;
	let onlyShowDerivedMarkets = false;
	let onlyShowNotResultedMarkets = false;
	let loadedDefaultMarketGroup = false;
	let marketGroupId = false;
	let keyMarketGroupId = false;
	let eventMarketGroupsLoaded = false;
	let loadingMarketGroup = false;
	let loadingEventMarketGroups = false;
	let showManualResult = false;
	let resultMarket;
	let showCreateNewMarket = false;
	let showAllMarketControls = false;
	let sgmPageNo = 1;
	let sgmPerPage = 50;
	let event_sport_id;
	let refetchMarketsCount = 0;
	let expandedMarkets = [];
	let filterMarketName = '';

	afterUpdate(() => {
		if (eventMarketGroupsLoaded == false && loadingEventMarketGroups == false) {
			getEventMarketGroups();
		}
	});

	$: event_id &&
		!loadedDefaultMarketGroup &&
		$MarketGroupStore.count() &&
		getMarketsForMarketGroup(
			$MarketGroupStore
				.where('sportevent_id', event_id)
				.where('is_key', false)
				.where('is_primary', false)
				.pluck('id')
				.first()
		);

	$: selectedMarketGroupOrder = collect([]);
	$: filteredMarkets =
		event_id &&
		markets &&
		selectedMarketGroupOrder &&
		(filterMarketName || !filterMarketName) &&
		(onlyShowMarketsWithBets || !onlyShowMarketsWithBets) &&
		(onlyShowManualMarkets || !onlyShowManualMarkets) &&
		(onlyShowNotResultedMarkets || !onlyShowNotResultedMarkets) &&
		(onlyShowDerivedMarkets || !onlyShowDerivedMarkets) &&
		filterMarkets(markets);
	$: sgmSelections =
		event_id &&
		(filterMarketName || !filterMarketName) &&
		$SportcastSelectionStore &&
		(onlyShowMarketsWithBets || !onlyShowMarketsWithBets) &&
		(onlyShowNotResultedMarkets || !onlyShowNotResultedMarkets) &&
		filterSGMSelections($SportcastSelectionStore);
	$: event_id && ClassificationStore.getClassifications(BetCategory.Sport);
	$: marketsCollapsed = false;
	$: (onlyShowMarketsWithBets || !onlyShowMarketsWithBets) &&
		(onlyShowNotResultedMarkets || !onlyShowNotResultedMarkets) &&
		marketGroupId === 'SGM' &&
		changeSGMFilters();

	function setEventSportId(event) {
		if (event && event.sport && event.sport.id && event_sport_id !== event.sport.id) {
			event_sport_id = event.sport.id;
		}
	}

	async function checkForFailedLoad() {
		if (refetchMarketsCount <= 10) {
			if ($MarketGroupStore.count()) {
				const primaryMarketGroup = $MarketGroupStore
					.where('sportevent_id', event_id)
					.where('is_key', false)
					.where('is_primary', false)
					.sortBy('order')
					.first();

				if (primaryMarketGroup?.name == undefined) {
					refetchMarketsCount++;
					await getEventMarketGroups();
				}
			}
		}
	}

	function getStatusLabel(market) {
		return !market.feed_status
			? `Status: ${market.status.name}`
			: market.override_status
				? `Override status: ${market.override_status.name}, Feed status: ${market.feed_status.name}`
				: `Feed status: ${market.feed_status.name}`;
	}

	function resetEvent() {
		loadedDefaultMarketGroup = false;
		marketGroupId = false;
		keyMarketGroupId = false;
		event_sport_id = undefined;
		return true;
	}

	async function getEventMarketGroups() {
		loadingEventMarketGroups = true;
		return SportEventStore.getEventMarketGroups(event_id)
			.then(() => {
				eventMarketGroupsLoaded = true;
			})
			.finally(() => {
				loadingEventMarketGroups = false;
			});
	}

	function getMarketsForMarketGroup(id = null) {
		if (!id) return;
		loadingMarketGroup = true;
		marketGroupId = id;
		loadedDefaultMarketGroup = true;
		SportBetLegStore.reset();
		MarketGroupStore.getMarketGroupDetails(marketGroupId, event_id).finally(() => (loadingMarketGroup = false));
		MarketGroupStore.getMarketGroup(marketGroupId.split('-')[1], true).then((result) => {
			selectedMarketGroupOrder = result.grouped_parent_markets.sortBy('order').pluck('market_type.id').unique();
		});
	}

	function changeSGMFilters() {
		sgmPageNo = 1;
		getSGMMarketsForEvent();
	}

	function getSGMMarketsForEvent() {
		marketGroupId = 'SGM';
		loadingMarketGroup = true;
		SportBetLegStore.reset();

		SportcastSelectionStore.getSportcastSelections(
			event,
			{ perPage: sgmPerPage, pageNo: sgmPageNo },
			{
				only_with_bets: onlyShowMarketsWithBets,
				not_resulted: onlyShowNotResultedMarkets
			}
		).finally(() => (loadingMarketGroup = false));
	}

	function setupKeyMarketGroup() {
		if ($MarketGroupStore) {
			keyMarketGroupId = $MarketGroupStore.where('sportevent_id', event_id).where('is_key', true).pluck('id').first();

			getKeyMarketGroup();
		}
	}

	function getKeyMarketGroup() {
		if (!keyMarketGroupId) return;
		MarketGroupStore.getMarketGroupDetails(keyMarketGroupId, event_id);
	}

	function getMarkets() {
		let marketIds = $MarketGroupStore.where('id', marketGroupId).pluck('market_ids').first();
		return marketIds && $MarketStore.whereIn('id', marketIds).sortBy((market) => marketIds.indexOf(market.id));
	}

	function filterMarkets(marketsToBeFiltered) {
		if (marketGroupId === 'SGM') {
			return [];
		}
		let marketsToFilter = marketsToBeFiltered ?? collect();

		if (onlyShowMarketsWithBets) {
			marketsToFilter = marketsToFilter.where('bet_count', '>', 0);
		}
		if (onlyShowManualMarkets) {
			marketsToFilter = marketsToFilter.where('is_manual', true);
		}
		if (onlyShowDerivedMarkets) {
			marketsToFilter = marketsToFilter.filter((market, key) => market.is_derived || market.has_derived);
		}
		if (onlyShowNotResultedMarkets) {
			marketsToFilter = marketsToFilter.where('status.id', '!=', MarketStatus.Resulted);
		}
		marketsToFilter = marketsToFilter.where('status.id', '!=', MarketStatus.Removed);

		const StatusSortOrder = {
			Open: MarketStatus.Open,
			Suspended: MarketStatus.Suspended,
			Closed: MarketStatus.Closed,
			Pending: MarketStatus.Pending,
			Resulted: MarketStatus.Resulted,
			Removed: MarketStatus.Removed
		};
		const statusSortOrder = Object.values(StatusSortOrder);

		marketsToFilter = marketsToFilter.sort((a, b) => {
			return (
				selectedMarketGroupOrder.items.indexOf(a.type.id) - selectedMarketGroupOrder.items.indexOf(b.type.id) ||
				statusSortOrder.indexOf((a.override_status && a.override_status.id) ?? a.status.id) -
					statusSortOrder.indexOf((b.override_status && b.override_status.id) ?? b.status.id) ||
				a.name.localeCompare(b.name) || // Sort alphabetically by market name
				a.id - b.id // Sort numerically by market ID (if theres multiple markets with the same name show the oldest first)
			);
		});

		if (filterMarketName) {
			marketsToFilter = marketsToFilter.filter((market, key) =>
				market.name.toLowerCase().includes(filterMarketName.toLowerCase())
			);
		}

		return marketsToFilter.all();
	}

	function filterSGMSelections(selectionsToBeFiltered) {
		if (marketGroupId !== 'SGM') {
			return [];
		}
		let selectionsToFilter = selectionsToBeFiltered ?? collect();

		if (onlyShowMarketsWithBets) {
			selectionsToFilter = selectionsToFilter.where('bet_count', '>', 0);
		}
		if (onlyShowNotResultedMarkets) {
			selectionsToFilter = selectionsToFilter.whereNotIn('status.id', [
				SportSelectionStatus.Win,
				SportSelectionStatus.Lost,
				SportSelectionStatus.Void,
				SportSelectionStatus.Resulted
			]);
		}

		return selectionsToFilter.all();
	}

	async function createMarketForEvent() {
		showCreateNewMarket = true;
		await tick();
		window.scrollTo(0, 0);
	}

	async function showAllMarketControlsForEvent() {
		showAllMarketControls = true;
		await tick();
		window.scrollTo(0, 0);
	}

	onMount(() => {
		Sockets.subscribeToSport();
	});

	onDestroy(() => {
		Sockets.unsubscribeFromSport();
	});

	function refreshEvent() {
		getEventMarketGroups();
		if (marketGroupId) {
			if (marketGroupId === 'SGM') {
				getSGMMarketsForEvent();
			} else {
				getMarketsForMarketGroup(marketGroupId);
			}
			return true;
		}
		getMarketsForMarketGroup(
			$MarketGroupStore
				.where('sportevent_id', event_id)
				.where('is_key', false)
				.where('is_primary', false)
				.pluck('id')
				.first()
		);
	}

	function changePageSGM(e) {
		if (e) {
			sgmPageNo = e.detail.page;
			getSGMMarketsForEvent();
		}
	}
	function expandMarket(marketId) {
		expandedMarkets.push(marketId);
		// Trick reactivity
		expandedMarkets = expandedMarkets;
	}

	function collapseMarkets(collapse) {
		if (collapse) {
			expandedMarkets = [];
		}
		marketsCollapsed = collapse;
		return true;
	}
	function loadMarketGroup(marketGroupId) {
		if (marketGroupId == 'SGM') {
			getSGMMarketsForEvent();
			return;
		}
		if (marketGroupId == undefined) {
			getEventMarketGroups();
		} else {
			getMarketsForMarketGroup(marketGroupId);
		}
	}
</script>

<Helmet title="Sport Trading" />

<Polling pollFunction={getEventMarketGroups} frequency={60} />
<Polling pollFunction={getKeyMarketGroup} frequency={60} />
{#if showManualResult}
	<ManualResult
		market_id={resultMarket.id}
		eventName={event.name}
		bind:open={showManualResult}
		on:resulted={() => MarketStore.getMarketWithSelections(resultMarket.id)}
	/>
{/if}
{#if showCreateNewMarket}
	<CreateMarket {event} bind:open={showCreateNewMarket} on:created={refreshEvent} />
{/if}
{#if showAllMarketControls && event.status.id != EventStatus.InPlay}
	<AllMarketControls
		{event}
		bind:open={showAllMarketControls}
		on:updated={() => {
			getEventMarketGroups();
			if (marketGroupId) {
				getMarketsForMarketGroup(marketGroupId);
				return true;
			}
			getMarketsForMarketGroup(
				$MarketGroupStore
					.where('sportevent_id', event_id)
					.where('is_key', false)
					.where('is_primary', false)
					.pluck('id')
					.first()
			);
		}}
	/>
{/if}

{#await loading}
	<Loading />
{:then finished}
	<div class="md:p-2 pt-0 md:mx-2 bg-white rounded">
		<div class="flex flex-col md:flex-row mx-auto md:space-x-4 max-w-custom">
			<LeftMenu />
			<!-- HEADER -->
			<div class="w-full md:w-5/6">
				<div class="sticky top-0 z-10 flex py-2 bg-white">
					<!-- <NextToJump /> -->
					{#if event}
						<div class="flex flex-col w-full">
							<div class="flex flex-col md:flex-row justify-between">
								<div>
									<div class="flex flex-row items-center">
										<Link
											href={`/sport?sport=${event.sport.id}`}
											title={`${event.sport.name} Events List`}
											class="flex cursor-pointer hover:underline text-grey items-center"
										>
											<Icon name="chevron-left" classes="w-2 mr-2" />
											<div class="mr-2 text-lg font-bold whitespace-nowrap">
												{event.sport.name}
											</div>
										</Link>
										<Link
											href={`/sport?sport=${event.sport.id}&comp=${event.competition.id}`}
											title={`${event.competition.name} Events List`}
											class="flex cursor-pointer hover:underline text-grey items-center"
										>
											<Icon name="chevron-left" classes="w-2 mr-2" />
											<div class="text-lg font-bold whitespace-nowrap">
												{event.competition.name} - Event List
											</div>
										</Link>
									</div>
								</div>
								<div class="flex flex-row items-center justify-center md:justify-end mt-2 md:mt-0 md:w-full w-svw">
									<PromotionButton
										{event}
										promotionTypes={[PromotionsType.SGMBonusBack, PromotionsType.SGMCashBack]}
										permission="sport_events.update.money_back_promotion"
									/>
									{#if event.status.id != EventStatus.InPlay}
										<button on:click={() => showAllMarketControlsForEvent()} class="btn btn-primary-action ml-2">
											All Market controls
										</button>
									{/if}
									<button on:click={() => createMarketForEvent()} class="btn btn-primary ml-2">
										Create manual market
									</button>
								</div>
							</div>
							<div class="flex flex-grow justify-center">
								<div class=" p-2 mt-2 border border-dark bg-white overflow-auto">
									<EventWithKeyMarkets {event} />
								</div>
							</div>
						</div>
					{/if}
				</div>
				<div class="mx-auto max-w-custom">
					<div class="flex mb-1 text-left md:text-center justify-center items-center">
						<div class="flex flex-row md:space-x-4">
							<CheckBox
								text="Only markets with bets"
								on:change={(e) => (onlyShowMarketsWithBets = e.target.checked)}
								checked={false}
							/>
							<CheckBox
								text="Only manual markets"
								on:change={(e) => (onlyShowManualMarkets = e.target.checked)}
								checked={false}
							/>
							<CheckBox
								text="Only derived markets"
								on:change={(e) => (onlyShowDerivedMarkets = e.target.checked)}
								checked={false}
							/>
							<CheckBox
								text="Only non-resulted markets"
								on:change={(e) => (onlyShowNotResultedMarkets = e.target.checked)}
								checked={false}
							/>
						</div>
					</div>
					<hr />
					<!-- MARKET FILTERING -->
					<div class="mt-1 flex flex-row justify-between items-center mb-1 pr-2">
						<div class="flex flex-row items-center">
							<TextInput
								name="market_name_filter"
								bind:value={filterMarketName}
								placeholder="Filter markets"
								inputClasses="h-8"
							/>
							<button class="ml-1 h-8 btn btn-muted" on:click={() => (filterMarketName = '')}>Clear</button>
						</div>
						<div class="flex flex-row items-center">
							<CheckBox
								text="Collapse all"
								on:change={(e) => collapseMarkets(e.target.checked)}
								checked={false}
								reverse={true}
							/>
						</div>
					</div>
					<!-- MARKET GROUPS -->
					<div class="mb-1 text-center w-full overflow-x-auto mb-1">
						<div class="btn-group">
							{#each $MarketGroupStore
								.where('sportevent_id', event_id)
								.where('is_key', false)
								.where('is_primary', false)
								.sortBy('order')
								.all() as marketGroup (marketGroup.id)}
								<button
									on:click={() => loadMarketGroup(marketGroup.id)}
									class="btn {marketGroupId === marketGroup.id ? 'btn-primary-action' : 'btn-muted text-dark'}"
								>
									{marketGroup.name}
								</button>
							{/each}
							{#if hasSGMMapped}
								<button
									on:click={() => loadMarketGroup('SGM')}
									class="btn {marketGroupId === 'SGM' ? 'btn-primary-action' : 'btn-muted text-dark'}"
								>
									SGM Markets
								</button>
							{/if}
						</div>
					</div>
					<!-- MARKET LIST -->
					{#if loadingMarketGroup}
						<Loading />
					{:else if marketGroupId === 'SGM'}
						{#if numberOfSGMPages > 1}
							<Pagination links={sgmLinks} on:click={changePageSGM} className="my-3" />
						{/if}
						<div>
							{#each sgmSelections as sgmSelection (sgmSelection.id)}
								<ManageSGMMarket
									{event}
									sportscastSelectionId={sgmSelection.id}
									{refreshEvent}
									on:refreshSGMSelections={getSGMMarketsForEvent}
								/>
							{/each}
						</div>
						{#if numberOfSGMPages > 1}
							<Pagination links={sgmLinks} on:click={changePageSGM} className="my-3" />
						{/if}
					{:else if markets}
						<div>
							{#each filteredMarkets as market (market.id)}
								{#if marketsCollapsed && !expandedMarkets.includes(market.id)}
									<div
										class="w-full pl-2 mb-1 border hover:bg-gray-100 bg-white flex flex-row justify-between select-none"
										on:click={() => expandMarket(market.id)}
									>
										<div class="text-left whitespace-nowrap py-1 flex flex-shrink justify-between">
											<div class="flex space-x-1 items-center">
												<div
													title={getStatusLabel(market)}
													class="w-4 h-4 {`bg-${
														StatusColours[
															market.feed_status && market.feed_status.id ? market.feed_status.id : market.status.id
														]
													}`} rounded-full cursor-help"
												>
													{#if market.override_status && market.override_status.id}
														<div
															class="h-2 {`bg-${
																StatusColours[market.override_status.id]
															}`} border-b border-white rounded-t-full"
														/>
													{/if}
												</div>
												<div>{market.name}</div>
											</div>
											<div class="pl-4 flex flex-shrink items-center">
												<div>
													{#if market.is_manual}
														{#if market.is_derived}
															<button disabled class="btn p-1.5 bg-purple-500 text-white rounded-full cursor-default">
																Derived
															</button>
														{:else}
															<button disabled class="btn p-1.5 bg-orange-500 text-white rounded-full cursor-default">
																Manual
															</button>
														{/if}
													{/if}
												</div>
											</div>
										</div>
										<div class="flex flex-row justify-center items-center pl-4 mr-6">
											{#if expandedMarkets.includes(market.id)}
												<Icon name="chevron-up" classes="block w-4 h-4 text-gray-800 inline" />
											{:else}
												<Icon name="chevron-down" classes="block w-4 h-4 text-gray-800 inline" />
											{/if}
										</div>
									</div>
								{:else}
									<ManageMarket
										{event}
										{market}
										{marketGroupId}
										bind:resultMarket
										bind:showManualResult
										derivedMarketGroups={$DerivedMarketGroupStore && $DerivedMarketGroupStore}
										on:refreshMarkets={refreshEvent}
									/>
								{/if}
							{/each}
						</div>
					{:else}
						<div class="py-4 text-center">No markets available for this group</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/await}
